import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import ResourceCard from "@/components/ResourceCard";
import FilterBar from "@/components/FilterBar";
import { Loader2, Inbox } from "lucide-react";
import { Link } from "react-router-dom";

export default function LibraryPage() {
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    keywords: [],
    sort: "date_desc",
  });
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: filters.sort };
      if (filters.search) params.search = filters.search;
      if (filters.type) params.resource_type = filters.type;
      if (filters.keywords.length > 0)
        params.keywords = filters.keywords.join(",");
      const data = await api.list(params);
      setResources(data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(fetchResources, 200);
    return () => clearTimeout(t);
  }, [fetchResources]);

  // Poll for in-progress items
  useEffect(() => {
    const hasInProgress = resources.some((r) =>
      ["pending", "extracting", "distilling"].includes(r.status),
    );
    if (!hasInProgress) return;
    const iv = setInterval(fetchResources, 3000);
    return () => clearInterval(iv);
  }, [resources, fetchResources]);

  return (
    <section
      className="max-w-6xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-20"
      data-testid="library-page"
    >
      <div className="flex flex-col gap-2 mb-10">
        <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500">
          La tua libreria
        </span>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">
          Libreria
        </h1>
        <p className="text-base text-zinc-600 max-w-xl">
          {resources.length > 0
            ? `${resources.length} ${resources.length === 1 ? "scheda distillata" : "schede distillate"}.`
            : "Ancora nessuna scheda distillata."}
        </p>
      </div>

      <FilterBar filters={filters} setFilters={setFilters} />

      <div className="mt-10">
        {loading ? (
          <div
            className="flex items-center justify-center py-24 text-zinc-400"
            data-testid="library-loading"
          >
            <Loader2 className="animate-spin" size={24} />
          </div>
        ) : resources.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-24 text-center"
            data-testid="library-empty"
          >
            <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
              <Inbox size={28} className="text-zinc-400" />
            </div>
            <h3 className="font-display text-xl font-medium mb-2">
              Nessuna risorsa trovata
            </h3>
            <p className="text-sm text-zinc-500 mb-6 max-w-sm">
              Prova a rimuovere i filtri oppure inizia distillando il tuo primo
              link.
            </p>
            <Link
              to="/"
              data-testid="library-empty-cta"
              className="inline-flex items-center rounded-full px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 transition-all"
              style={{ backgroundColor: "#002FA7" }}
            >
              Distilla una risorsa
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((r) => (
              <ResourceCard key={r.id} resource={r} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
